import dayjs, { Dayjs } from "dayjs";
import { DateValue } from "@mantine/dates";
import {
    MAX_HOURS,
    MAX_MILLISECONDS,
    MAX_MINUTES_OR_SECONDS,
    RelativeDates,
    RelativeDateInfo,
} from "./date-picker-input.constants";

/**
 * Returns the full map of relative date presets, each with a fresh start/end date
 * calculated at call time. Called as a function rather than a constant to prevent
 * stale dates when the page stays open across midnight or long periods.
 * @returns {Record<RelativeDates, RelativeDateInfo>} Map of all relative date presets.
 */
export const getRelativeDatesInfo = (): Record<RelativeDates, RelativeDateInfo> => ({
    [RelativeDates.Today]: {
        name: RelativeDates.Today,
        formatedName: "Today",
        startDate: new Date(new Date().setHours(0, 0, 0, 0)),
        endDate: new Date(new Date().setHours(23, 59, 59, 999)),
    },
    [RelativeDates.Yesterday]: {
        name: RelativeDates.Yesterday,
        formatedName: "Yesterday",
        startDate: (() => {
            const startDate = new Date();
            startDate.setDate(startDate.getDate() - 1);
            startDate.setHours(0, 0, 0, 0);
            return startDate;
        })(),
        endDate: (() => {
            const endDate = new Date();
            endDate.setDate(endDate.getDate() - 1);
            endDate.setHours(23, 59, 59, 999);
            return endDate;
        })(),
    },
    [RelativeDates.Last7Days]: {
        name: RelativeDates.Last7Days,
        formatedName: "Last 7 days",
        startDate: dayjs().subtract(6, "day").startOf("day").toDate(),
        endDate: dayjs().endOf("day").toDate(),
    },
    [RelativeDates.Last14Days]: {
        name: RelativeDates.Last14Days,
        formatedName: "Last 14 days",
        startDate: dayjs().subtract(13, "day").startOf("day").toDate(),
        endDate: dayjs().endOf("day").toDate(),
    },
    [RelativeDates.Last30Days]: {
        name: RelativeDates.Last30Days,
        formatedName: "Last 30 days",
        startDate: dayjs().subtract(29, "day").startOf("day").toDate(),
        endDate: dayjs().endOf("day").toDate(),
    },
    [RelativeDates.LastWeek]: {
        name: RelativeDates.LastWeek,
        formatedName: "Last Week",
        startDate: (() => {
            const date = new Date();
            const day = date.getDay();
            date.setDate(date.getDate() - day - 6); // Start of last week
            date.setHours(0, 0, 0, 0);
            return date;
        })(),
        endDate: (() => {
            const date = new Date();
            const day = date.getDay();
            date.setDate(date.getDate() - day); // End of last week
            date.setHours(23, 59, 59, 999);
            return date;
        })(),
    },
    [RelativeDates.LastMonth]: {
        name: RelativeDates.LastMonth,
        formatedName: "Last Month",
        startDate: (() => {
            const date = new Date();
            date.setMonth(date.getMonth() - 1, 1); // First day of last month
            date.setHours(0, 0, 0, 0);
            return date;
        })(),
        endDate: (() => {
            const date = new Date();
            date.setDate(0); // Last day of last month
            date.setHours(23, 59, 59, 999);
            return date;
        })(),
    },
    [RelativeDates.WeekToDate]: {
        name: RelativeDates.WeekToDate,
        formatedName: "Week to date",
        startDate: (() => {
            const date = new Date();
            const day = date.getDay();
            date.setDate(date.getDate() - day + 1); // Start of week to date is Monday, not Sunday
            date.setHours(0, 0, 0, 0);
            return date;
        })(),
        endDate: new Date(),
    },
    [RelativeDates.MonthToDate]: {
        name: RelativeDates.MonthToDate,
        formatedName: "Month to date",
        startDate: (() => {
            const date = new Date();
            date.setDate(1);
            date.setHours(0, 0, 0, 0);
            return date;
        })(),
        endDate: new Date(),
    },
});

export const formatDate = (date: DateValue): string => {
    return dayjs(date).format("DD MMM YYYY");
};

/**
 * Safely converts a DateValue to a Date object, ensuring it's parsed as local time.
 * This prevents timezone-related off-by-one-day issues when parsing date strings.
 * @param {DateValue} dateValue - The date value to convert (can be Date, string, or null)
 * @returns {Date | null} The Date object or null if input is null/undefined
 */
export const toLocalDate = (dateValue: DateValue): Date | null => {
    if (!dateValue) return null;

    // If it's already a Date object, return a copy
    if (dateValue instanceof Date) {
        return new Date(dateValue);
    }

    // If it's a string, parse it explicitly as local time using dayjs
    if (typeof dateValue === "string") {
        return dayjs(dateValue).toDate();
    }

    return null;
};

/**
 * Combines a calendar date value with a `HH:mm` time string into one local `Date`.
 * When applying an end date at `23:59`, it sets seconds/milliseconds to `59.999`
 * so range filters remain inclusive through the final millisecond of that minute.
 *
 * @param {DateValue} dateValue - Selected date from the date picker.
 * @param {string} [time] - Time string in `HH:mm` format from the time picker.
 * @param {boolean} [isEndDate=false] - Whether this is the end of a selected range.
 * @returns {Date | null} Local date-time value or `null` when date is not available.
 */
export const getDateTimeValue = (dateValue: DateValue, time?: string, isEndDate = false): Date | null => {
    const dateTime = toLocalDate(dateValue);

    if (!dateTime || !time) {
        return dateTime;
    }

    const [hours, minutes] = time.split(":").map(Number);
    const shouldSetSecondsAndMilliseconds = isEndDate && hours === MAX_HOURS && minutes === MAX_MINUTES_OR_SECONDS;

    dateTime.setHours(
        hours,
        minutes,
        shouldSetSecondsAndMilliseconds ? MAX_MINUTES_OR_SECONDS : 0,
        shouldSetSecondsAndMilliseconds ? MAX_MILLISECONDS : 0
    );

    return dateTime;
};

/**
 * Calculates the minimum date based on the provided parameters.
 * @param {Dayjs} minDate - The earliest selectable date.
 * @param {number} limitPastDays - The number of days to limit the past.
 * @param {number} limitSelectionMonths - The number of months to limit the selection.
 * @param {Dayjs | null} rangeStart - The start date of the range.
 * @param {Dayjs} now - The current date.
 * @returns {Date | undefined} The minimum date.
 */
export const getMinDate = ({
    minDate,
    limitPastDays,
    limitSelectionMonths,
    rangeStart,
    now = dayjs(),
}: {
    minDate?: Dayjs;
    limitPastDays?: number;
    limitSelectionMonths?: number;
    rangeStart?: Dayjs | null;
    now?: Dayjs;
}): Date | undefined => {
    // 1. Calculate the absolute limit based on `limitPastDays` (always applies if defined)
    const limitedPastDate = limitPastDays ? now.subtract(limitPastDays, "day") : null;
    let absoluteMinDate = limitedPastDate ?? minDate ?? null;

    if (limitedPastDate && minDate) {
        absoluteMinDate = limitedPastDate.isAfter(minDate) ? limitedPastDate : minDate;
    }

    // 2. If no range is currently being selected (no start date),
    //    the only limit is the absolute one (if it exists).
    if (!rangeStart) {
        return absoluteMinDate?.toDate();
    }

    // 3. If a range is started, we might also have a relative limit based on `limitSelectionMonths`.
    //    This limits how far BACK from the standard start date one can go.
    //    NOTE: Typically 'limitSelectionMonths' limits the *duration*, so it usually affects maxDate.
    //    However, if the user picks the *end* date first (uncommon but possible in some UX), or picks a 'middle' date,
    //    this logic effectively creates a window around the first selection.
    //    Based on requirements: "every dates older or later than 1 months from the first selected date are disabled"
    const relativeMinDate = limitSelectionMonths ? rangeStart.subtract(limitSelectionMonths, "month") : null;

    // 4. Return the most restrictive (latest) date between the two limits.
    if (absoluteMinDate && relativeMinDate) {
        return absoluteMinDate.isAfter(relativeMinDate) ? absoluteMinDate.toDate() : relativeMinDate.toDate();
    }

    return (absoluteMinDate || relativeMinDate)?.toDate();
};

/**
 * Calculates the maximum date based on the provided parameters.
 * @param {Dayjs} maxDate - The maximum date.
 * @param {number} limitSelectionMonths - The number of months to limit the selection.
 * @param {Dayjs | null} rangeStart - The start date of the range.
 * @returns {Date | undefined} The maximum date.
 */
export const getMaxDate = ({
    maxDate,
    limitSelectionMonths,
    rangeStart,
}: {
    maxDate: Dayjs;
    limitSelectionMonths?: number;
    rangeStart?: Dayjs | null;
}): Date | undefined => {
    // 1. The absolute max limit is always the provided `maxDate` (defaults to today in component).
    const absoluteMaxDate = maxDate;

    // 2. If no range is currently being selected, the only limit is the absolute one.
    if (!rangeStart) {
        return absoluteMaxDate.toDate();
    }

    // 3. If a range is started, apply the relative window limit forward.
    const relativeMaxDate = limitSelectionMonths ? rangeStart.add(limitSelectionMonths, "month") : null;

    // 4. Return the most restrictive (earliest) date between the two limits.
    if (relativeMaxDate) {
        return absoluteMaxDate.isBefore(relativeMaxDate) ? absoluteMaxDate.toDate() : relativeMaxDate.toDate();
    }

    return absoluteMaxDate.toDate();
};

export const getFutureClosestDate = (...args: (Dayjs | null)[]) => {
    return args.reduce((prev, current) => (prev?.isBefore(current) || current === null ? prev : current));
};

/**
 * Adds custom day props to a date. Currently used to add first and last day of month data attribute for styling
 * @param date - The date to add custom day props to.
 * @returns The added day props.
 */
export const addCustomDayProps = (date: string) => {
    const dateObj = dayjs(date).toDate();
    const dayOfMonth = dateObj.getDate();
    const year = dateObj.getFullYear();
    const month = dateObj.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const addedDayProps: Record<string, string> = {};

    if (dayOfMonth === 1) {
        addedDayProps["data-first-day-of-month"] = "true";
    }
    if (dayOfMonth === daysInMonth) {
        addedDayProps["data-last-day-of-month"] = "true";
    }

    return addedDayProps;
};
